<template>
  <b-container
    fluid
    class="bop-setup custom-container"
    :key="wizardKey"
  >
    <template v-if="isLoaded">
      <div
        v-if="!saveFinished"
        class="d-flex justify-content-center mb-3 height-60vh loading"
      >
        <b-spinner
          label="Loading..."
          class="margin-auto"
        ></b-spinner>
      </div>
      <Wizard
        v-show="isVisible && isGeneralStepLoaded"
        :steps="steps"
        :onNext="next"
        :onPrevious="previous"
        @save="save"
        :saveButtonText="saveButtonText"
        :showSave="
          (isEditable && isSaveVisible && !isAwaitingApproval) ||
          (showSaveForOtpValidation && stepIndex === summaryStepIndex)
        "
        :startIndex="startIndex"
        :key="`${bop.id}_${startIndex}_${language}`"
        :saveDisabled="
          saveDisabled ||
          (!this.otpInfoSynthesisValid &&
            showSaveForOtpValidation &&
            stepIndex === summaryStepIndex)
        "
        :sendDisabled="sendDisabled"
        :isExportingPdf="isExportingPdf"
        :bopStatus="bop.bopStatusKey"
        :canValidateBop="canValidateBop"
        :canApproveBop="userCanApproveBop"
        :otpInfoSynthesisValid="otpInfoSynthesisValid"
        @exportBopPdf="exportBopPdf"
        @exportFebPdf="exportFebPdf"
        @approveBop="approveBop"
        @declineBop="declineBop"
        ref="bopSetupWizard"
      >
        <template #generalInformations>
          <GeneralInformationsStep
            :isEditable="isEditable"
            @bopmodel-changed="onBopModelChanged"
            @fully-loaded="isGeneralStepLoaded = true"
            @openSubBopsModal="openSubBopsModal"
          ></GeneralInformationsStep>
        </template>
        <template #operationPrincipal>
          <template
            v-if="
              bop.bopModelId &&
              $store.state.selectedBopModel &&
              operationPrinciaplLoaded
            "
          >
            <OperationPrincipalCalAppareilStep
              v-if="isCalorifugeageAppareilBop"
              :isNotReadOnly="isEditable"
              @fully-loaded="onFullyLoaded"
            />
            <OperationPrincipalStepOpFamily
              v-else-if="haveFamilyGrouping"
              :isNotReadOnly="isEditable"
              @fully-loaded="onFullyLoaded"
              @openSubBopsModal="openSubBopsModal"
            />
            <OperationPrincipalCalTuyStep
              v-else-if="isCalorifugeageTuyauterieBop"
              :isNotReadOnly="isEditable"
              @fully-loaded="onFullyLoaded"
              @caloTuyauterieOperationsChange="onCaloTuyauterieOperationsChange"
            />
            <OperationPrincipalStep
              v-else
              :isNotReadOnly="isEditable"
              @fully-loaded="onFullyLoaded"
            />
            <OperationPrincipalStepFile
              :isEditable="isEditable"
              :refreshTrigger="fileRefreshKey"
              @files-changed="fileRefreshKey++"
              v-if="isOperationStepLoaded"
            />
            <div
              v-if="!isOperationStepLoaded"
              class="d-flex justify-content-center mb-3 height-60vh"
            >
              <b-spinner
                label="Loading..."
                class="margin-auto"
              ></b-spinner>
            </div>
          </template>
        </template>
        <template #operationDiverse>
          <template v-if="bop.bopModelId && bop.typeTravaux">
            <OperationDiverseStep
              @openSubBopsModal="openSubBopsModal"
              :isNotReadOnly="isEditable"
            />
          </template>
        </template>

        <template #feb>
          <FebStep
            @openSubBopsModal="openSubBopsModal"
            :isEditable="isEditable"
            :fileRefreshTrigger="fileRefreshKey"
            @files-changed="fileRefreshKey++"
          />
        </template>
        <template #operationSupp>
          <template v-if="bop.bopModelId && bop.siteId && bop.typeTravaux">
            <OperationSuppStep
              @openSubBopsModal="openSubBopsModal"
              :isNotReadOnly="isEditable"
            />
          </template>
        </template>
        <template #summary>
          <BopSummaryStep
            :isEditable="isEditable"
            @openSubBopsModal="openSubBopsModal"
            @otp-info-valid-changed="onOtpInfoValidChanged"
            :canApprove="userCanApproveBop"
          />
        </template>
        <template #facturationSap>
          <FacturationStep
            @openSubBopsModal="openSubBopsModal"
            :isValidation="true"
            :isReadOnly="bop.isCreatedBySap && !isEditable"
            :showVisa="false"
          />
        </template>
        <template #facturation>
          <FacturationStep
            @openSubBopsModal="openSubBopsModal"
            :isReadOnly="
              (bop.isCreatedBySap && !isEditable) ||
              bop.bopStatusKey !== 'AcceptLaunchWork'
            "
          />
        </template>
      </Wizard>
    </template>

    <div
      v-if="!isVisible || !isLoaded || !isGeneralStepLoaded"
      class="d-flex justify-content-center mb-3 height-60vh"
    >
      <b-spinner
        label="Loading..."
        class="margin-auto"
      ></b-spinner>
    </div>
    <SubBopsModal
      :bop="bop"
      :isMultiEchafaudage="isMultiEchafaudage"
      :isReadOnly="!isEditable"
    />
  </b-container>
</template>

<script lang="ts">
import Bop from "@/models/Bop";
import BopOperation from "@/models/BopOperation";
import Feb from "@/models/Feb";
import OperationGroup from "@/models/OperationGroup";
import SubBop from "@/models/SubBop";
import User from "@/models/User";
import Validation from "@/models/Validation";
import router from "@/router";
import BopArchiveService from "@/services/BopArchiveService";
import BopModelService from "@/services/BopModelService";
import BopService from "@/services/BopService";
import BopStatusService from "@/services/BopStatusService";
import CoefficientService from "@/services/CoefficientService";
import CoefficientSelectedValueService from "@/services/CoefficientSelectedValueService";
import SapApiService from "@/services/SapApiService";
import SitesServices from "@/services/SitesServices";
import TypeTravauxService from "@/services/TypeTravauxService";
import userService from "@/services/UserService";
import Constants from "@/utils/Constants";
import {
  BopModelEnum,
  BopStatus,
  CoefficientTypeEnum,
  OperationType,
} from "@/utils/Enums";
import table from "@/utils/table";
import { Guid } from "guid-typescript";
import { Component,Vue,Watch } from "vue-property-decorator";
import CoefficientOptionService from "@/services/CoefficientOptionService";
const TYPETRAVAUX_MAINTENANCE_KEY = "maintenance";

type requiredField = {
  translationKey: string;
  elementKey: string;
  isRequiredOnSimulation: boolean;
  validation: string | null;
  skipWhenGardeCorps?: boolean;
};
type step = {
  slot: string;
  title: string;
  tooltip?: string;
  requiredFieldsKeys: Array<requiredField>;
};
const FAMILY_GROUPED_BOP_MODELS: Array<string> = [
  BopModelEnum.Echafaudage,
  BopModelEnum.MultiEchafaudage,
  BopModelEnum.PeintureIndustrielle,
  BopModelEnum.NettoyageIndustrielle,
];
const VALIDATION_FORMULAS = {
  strictPositifNumber: "{p}",
};
@Component({
  components: {
    Wizard: () => import("@/components/Shared/wizard/Wizard.vue"),
    GeneralInformationsStep: () =>
      import("@/components/BopSetup/GeneralInformationsStep.vue"),
    OperationDiverseStep: () =>
      import("@/components/BopSetup/OperationDiverseStep.vue"),
    OperationPrincipalStep: () =>
      import("@/components/BopSetup/OperationPrincipalStep.vue"),
    OperationPrincipalStepFile: () =>
      import("@/components/BopSetup/OperationPrincipalStepFile.vue"),
    OperationPrincipalStepOpFamily: () =>
      import("@/components/BopSetup/OperationPrincipalStepOpFamily.vue"),
    OperationPrincipalCalAppareilStep: () =>
      import(
        "@/components/BopSetup/CalorifugeageAppareil/OperationPrincipalCaStep.vue"
      ),
    OperationSuppStep: () =>
      import("@/components/BopSetup/OperationSuppStep.vue"),
    BopSummaryStep: () => import("@/components/BopSetup/BopSummaryStep.vue"),
    FacturationStep: () => import("@/components/BopSetup/FacturationStep.vue"),
    FebStep: () => import("@/components/BopSetup/FebStep.vue"),
    OperationPrincipalCalTuyStep: () =>
      import(
        "@/components/BopSetup/CalorifugeageTuyauterie/OperationPrincipalCalTuyStep.vue"
      ),
    SubBopsModal: () => import("@/components/BopSetup/SubBopsModal.vue"),
  },
})
export default class BopSetup extends Vue {
  public saveFinished: boolean = true;

  public wizardKey: string = Guid.create().toString();
  public saveButtonText: string = "";
  public stepIndex: number = 0;
  public startIndex: number = 0;
  public isLoaded: boolean = false;
  public isVisible: boolean = false;
  public saveDisabled: boolean = false;
  public steps: Array<step> = [];
  public isOperationStepLoaded: boolean = false;
  public operationPrinciaplLoaded = false;
  public caloTuyauterieOperations: Array<any> = [];
  public isGeneralStepLoaded = false;
  public isExportingPdf: boolean = false; // State to manage loading spinner
  public fileRefreshKey: number = 0;
  public typeTravaux: Array<any> = [];
  public currentUser: User = userService.getCurrentuser();
  public userCanApproveBop: boolean = false;
  public otpInfoSynthesisValid: boolean = true;
  public showSaveForOtpValidation: boolean = false;
  public onFullyLoaded() {
    this.isOperationStepLoaded = true;
    setTimeout(() => {
      this.$store.commit("changeHasBeenModified", false);
    }, 400);
  }
  public openSubBopsModal() {
    //When opening subBopsModal, run a validation on all subBops to know wether it is valid or not
    for (const feb of this.bop.febs!) {
      for (const subBop of feb.subBops!) {
        let status = this.validateBop(this.bop, subBop || undefined, false);
        if (subBop) {
          subBop.isValid = status;
        }
      }
    }

    (this as any).$bvModal.show("subbops-modal");
  }

  public onOtpInfoValidChanged(isValid: boolean) {
    this.otpInfoSynthesisValid = isValid;
  }
  public get language(): string {
    return this.$i18n.locale;
  }
  public isSaveVisible = true;
  public get haveFamilyGrouping() {
    return FAMILY_GROUPED_BOP_MODELS.includes(this.bop.bopModelKey);
  }
  public get sendDisabled(): boolean {
    //Le bouton [envoyer vers Arkema] reste disponible dans le processus 2:
    // tant qu’un BOP est en statut « en attente » il est possible à tout moment de le modifier et de le renvoyer.
    if (
      this.bop.isCreatedBySap &&
      this.summaryStepIndex !== this.stepIndex &&
      this.bop.bopStatusKey === BopStatus.WaitingForValidation
    ) {
      return true;
    }
    if (
      (this.bop.totalOperationSuppHeurePoints +
        this.bop.totalSuppHauteurPoints ===
        0 ||
        this.facturationValidation.sapSupplementServiceNumber.length) &&
      //epi validation
      (this.bop.totalOperationSuppEpiPoints === 0 ||
        this.facturationValidation.sapEPIServiceNumber.length) &&
      //general service validation
      (this.bop.totalOperationPrincipalPoints +
        this.bop.totalOperationDiversePoints ===
        0 ||
        this.facturationValidation.sapPrestationServiceNumber.length)
    ) {
      return false;
    } else {
      if (this.bop.isCreatedBySap && this.summaryStepIndex === this.stepIndex)
        return true;
    }

    return false;
  }
  public get isCalorifugeageAppareilBop() {
    return this.bop.bopModelKey === BopModelEnum.CalorifugageAppareil;
  }
  public get isCalorifugeageTuyauterieBop() {
    return this.bop.bopModelKey === BopModelEnum.CalorifugageTuyauterie;
  }
  public get bop(): Bop {
    return this.$store.state.bop;
  }
  public get isMultiEchafaudage(): boolean {
    return this.bop.bopModelKey === BopModelEnum.MultiEchafaudage;
  }
  private get selectedFebId(): string | null {
    return (this.$store.state.selectedFebId as string) || null;
  }
  private get selectedSubBopId(): string | null {
    return (this.$store.state.selectedSubBopId as string) || null;
  }
  private get selectedFeb(): Feb | null {
    return this.$store.getters.selectedFeb;
  }
  public get selectedSubBop(): SubBop | null {
    return this.$store.getters.selectedSubBop;
  }
  private getActiveOpsContainer(): any {
    return this.selectedSubBop ?? this.bop;
  }
  public get isSimulation(): boolean {
    return this.bop.bopStatusKey === BopStatus.Simulation;
  }
  public get isEditable(): boolean {
    return !Bop.readOnlyStatuses.includes(this.bop.bopStatusKey);
  }
  public get isAwaitingApproval(): boolean {
    return this.bop.bopStatusKey === BopStatus.AwaitingLaunchWorkApproval;
  }
  private get isApproved(): boolean {
    return this.bop.bopStatusKey === BopStatus.AcceptLaunchWork;
  }
  private async isApprovedEditable(): Promise<boolean> {
    const typeTravauxMaintenance = (
      await TypeTravauxService.getTypeTravauxByKey(
        TYPETRAVAUX_MAINTENANCE_KEY,
        this.$i18n.locale,
      )
    ).data;
    return (
      this.isApproved &&
      this.$store.getters.userRoleKey == "orderGiver" &&
      !this.bop.isCreatedBySap &&
      this.bop.typeTravaux !== typeTravauxMaintenance.id
    );
  }
  private get isEdit(): boolean {
    return (
      this.bop.bopStatusKey === BopStatus.Finished &&
      this.$route.query.isEdit !== undefined &&
      this.$route.query.isEdit === "true"
    );
  }
  private get isFacturationVisibleForOrderGiver(): boolean {
    return !(this.$store.getters.userRoleKey == "orderGiver");
  }
  public get summaryStepIndex(): number {
    return this.steps.findIndex((s) => s.slot === "summary");
  }
  private get facturationStepIndex(): number {
    return this.steps.findIndex(
      (s) => s.slot === this.facturationStepConfig.slot,
    );
  }
  public get canValidateBop(): boolean {
    return !this.isSimulation && this.stepIndex === this.summaryStepIndex;
  }
  private operationPrincipalGroups: Array<any> = [];
  private get facturationStepConfig(): step {
    return {
      slot: "facturation",
      title: this.$t("bop.facturation.stepTitle").toString(),
      requiredFieldsKeys: [],
    };
  }
  private get facturationSapStepConfig(): step {
    return {
      slot: "facturationSap",
      title: this.$t("bop.facturationSap.stepTitle").toString(),
      requiredFieldsKeys: [],
    };
  }
  private get febStepConfig(): step {
    return {
      slot: "feb",
      title: this.$t("bop.feb.stepTitle").toString(),
      tooltip: this.$t("bop.feb.stepTooltip").toString(),
      requiredFieldsKeys: [
        {
          translationKey: "bopSetup.feb.nombrePlancherTravail",
          elementKey: "feb.nombrePlancherTravail",
          isRequiredOnSimulation: false,
          validation: VALIDATION_FORMULAS.strictPositifNumber,
          skipWhenGardeCorps: true,
        },
        {
          translationKey: "bopSetup.feb.chargeSpecifiquePlanchers",
          elementKey: "feb.chargeSpecifiquePlanchers",
          isRequiredOnSimulation: false,
          validation: VALIDATION_FORMULAS.strictPositifNumber,
          skipWhenGardeCorps: true,
        },
      ],
    };
  }
  private get facturationValidation(): Validation {
    return this.$store.state.validation;
  }
  private get isCreatedBySapAndEditable() {
    return (
      this.bop.isCreatedBySap &&
      (<Array<string>>[
        BopStatus.Initial,
        BopStatus.Simulation,
        BopStatus.WaitingForValidation,
      ]).includes(this.bop.bopStatusKey)
    );
  }
  private get isTuyauterieMetalique(): boolean {
    if (this.bop.bopModelKey === BopModelEnum.TuyauterieMetalique) return true;
    else return false;
  }
  private get isEchafaudage(): boolean {
    return this.bop.bopModelKey === BopModelEnum[BopModelEnum.Echafaudage];
  }
  /**
   * #######
   * Methods
   * #######
   */
  private upsertGlobalCoefficient(key: string, options: any[], coeffId: any) {
    const opGlobal = this.$store.state.operationPrincipalGlobalCoefficients || {};

    const mapped = options.map(o => ({
      key: { id: o.id, value: o.value, coefficientId: o.coefficientId },
      id: o.id,
      value: o.label,
    }));

    const existing = opGlobal[key] || {};

    const mergedMap = new Map(
      [...(existing.options || []), ...mapped].map(o => [o.id, o])
    );

    const newDef = {
      ...existing,
      id: coeffId,
      name: this.$t('Environnements spécifiques').toString(),
      coeffKey: key,
      options: Array.from(mergedMap.values()),
    };

    this.$store.commit('changeOperationPrincipalGlobalCoefficients', {
      ...opGlobal,
      [key]: newDef,
    });
  }
  private clearEnvOptions() {
    this.$store.commit('changeEnvSpecificOptions', []);

    const opGlobal = this.$store.state.operationPrincipalGlobalCoefficients || {};
    const cloned = { ...opGlobal };

    delete cloned['EnvKey'];

    this.$store.commit('changeOperationPrincipalGlobalCoefficients', cloned);
  }
  private refreshWizard() {
    this.wizardKey = Guid.create().toString();
  }
  public async onBopModelChanged(bm: any) {
    if (!this.isEditable) return;
    if (this.bop.bopModelId === null) {
      this.bop.bopModelId = bm.bopModel.id;
      this.bop.bopModelName = bm.translation.name;
      if (
        this.isMultiEchafaudage &&
        !this.selectedSubBopId &&
        this.bop.febs?.length
      ) {
        const firstFeb = this.bop.febs[0] ?? null;
        const firstSubBop = firstFeb.subBops?.[0] || null;
        // this.$emit('update:selectedSubBopId', firstSubBop?.id || null);

        // this.$emit('update:selectedFebId', firstFeb?.id || null);
        this.$store.commit("changeSelectedSubBopId", firstSubBop?.id || null);
        this.$store.commit("changeSelectedFebId", firstFeb?.id || null);
      }
      this.$store.commit("changeSelectedBopModel", bm.bopModel);
      this.bop.bopModelKey = bm.bopModel.key;
    } else {
      const modalResponse = await this.$bvModal.msgBoxConfirm(
        this.$t("bopConfirmChangeBopModel.message").toString(),
        {
          title: this.$t("bopConfirmChangeBopModel.title").toString(),
          size: "lg",
          buttonSize: "lg",
          okVariant: "primary",
          cancelVariant: "secondary",
          okTitle: this.$t("bopConfirmChangeBopModel.yes").toString(),
          cancelTitle: this.$t("bopConfirmChangeBopModel.no").toString(),
          footerClass: "p-2",
          hideHeaderClose: false,
          dialogClass: "modal-app",
          centered: true,
        },
      );
      if (modalResponse) {
        this.bop.bopModelId = bm.bopModel.id;
        this.bop.bopModelName = bm.translation.name;
        if (
          this.isMultiEchafaudage &&
          !this.selectedSubBopId &&
          this.bop.febs?.length
        ) {
          const firstFeb = this.bop.febs[0] ?? null;
          const firstSubBop = firstFeb.subBops?.[0] || null;
          // this.$emit('update:selectedSubBopId', firstSubBop?.id || null);

          // this.$emit('update:selectedFebId', firstFeb?.id || null);
          this.$store.commit("changeSelectedSubBopId", firstSubBop?.id || null);
          this.$store.commit("changeSelectedFebId", firstFeb?.id || null);
        }
        this.$store.commit("changeSelectedBopModel", bm.bopModel);
        this.bop.bopModelKey = bm.bopModel.key;
        const container: any = this.getActiveOpsContainer();
        container.operationPrincipalGroups = [];
        container.operationDiverseGroups = [new OperationGroup()];
        container.operationSuppGroups = [new OperationGroup()];
        container.operationPrincipalGroups.forEach(
          (og: OperationGroup) => (og.canBeDeleted = true),
        );
        container.selectedGlobalCoefficients = {};
        this.$store.commit("changeTotalOperationPrincipalPoints", 0);
        this.$store.commit("changeTotalOperationSuppEpiPoints", 0);
        this.$store.commit("changeTotalOperationSuppHeurePoints", 0);
        this.$store.commit("changetotalSuppHauteurPointsPoints", 0);
        this.$store.commit("changeTotalOperationDiversePoints", 0);
        this.$store.commit("changeSelectedGlobalCoefficients", {});
        this.globalCoefficients = (
          await CoefficientService.getCoefficientByTypeAndBopModel(
            bm.bopModel.id,
            CoefficientTypeEnum.T4,
            this.language,
          )
        ).data;
      }
    }
  }
  private getBopValue(path: string) {
    return table.findByPath(this.bop, path);
  }
  private evaluateValidationFormula(validationFormula, fieldValue) {
    switch (validationFormula) {
      case VALIDATION_FORMULAS.strictPositifNumber:
        return parseFloat(fieldValue) > 0;
    }
    return new RegExp(validationFormula).test(fieldValue);
  }
  private validateStep(stepIndex, showNotification = true): boolean {
    const fieldsToValidate = this.steps[stepIndex].requiredFieldsKeys;
    for (let i = 0; i < fieldsToValidate.length; i++) {
      const field = fieldsToValidate[i];
      if (
        field.skipWhenGardeCorps &&
        (this.bop.feb?.echafaudageGardeCorps ||
          (this.isMultiEchafaudage && this.selectedFeb?.echafaudageGardeCorps))
      ) {
        continue;
      }
      let fieldPath = field.elementKey;
      // Adjust field path for multi-echafaudage BOPs
      if (
        this.isMultiEchafaudage &&
        field.elementKey.indexOf("feb.") === 0 &&
        this.selectedFeb
      ) {
        let febIndex = this.bop.febs?.indexOf(this.selectedFeb) ?? 0;
        fieldPath = field.elementKey.replace("feb.", `febs[${febIndex}].`);
      }
      const fieldValue = this.getBopValue(fieldPath);
      const isAlwaysRequired =
        (this.isSimulation && field.isRequiredOnSimulation) ||
        !this.isSimulation;
      if (
        isAlwaysRequired &&
        (!fieldValue ||
          (field.validation &&
            !this.evaluateValidationFormula(field.validation, fieldValue)))
      ) {
        if (showNotification) {
          this.notify(
            this.$t("bopSetup.validationErrorTitle").toString(),
            this.$t("bopSetup.validationErrorText").toString() +
              " " +
              this.$t(field.translationKey).toString(),
            "error",
          );
        }
        return false;
      }
    }
    return true;
  }
  private clearNotifications() {
    this.$notify({
      group: "globalError",
      clean: true,
    });
  }
  private setNavigationBehavour(stepIndex: number, nextStepIndex: number) {
    const defaultText = this.$t("wizard.save").toString();
    if (this.canValidateBop && !this.showSaveForOtpValidation) {
      this.saveButtonText = this.$t("wizard.validate").toString();
    } else {
      this.saveButtonText = defaultText;
    }
  }

  public next(stepIndex: number, nextStepIndex: number) {
    this.stepIndex = nextStepIndex;
    for (let i = stepIndex; i < nextStepIndex; i++) {
      if (!this.validateStep(i)) {
        (<any>this.$refs.bopSetupWizard).navigate(i);
        return false;
      }
    }
    this.setNavigationBehavour(stepIndex, nextStepIndex);
    // this.isOperationStepLoaded = false;
    // this.clearNotifications();
    return true;
  }
  public previous(stepIndex: number, nextStepIndex: number) {
    this.stepIndex = nextStepIndex;
    this.setNavigationBehavour(stepIndex, nextStepIndex);
    // this.isOperationStepLoaded = false;
    // this.clearNotifications();
    return true;
  }

  private notify(title: string, text: string, type: string) {
    const isError = type === "error";
    let notifObject: any = {
      group: isError ? "globalError" : "bopNotificationGroup",
      type,
      title,
      text,
    };
    if (type === "error") {
      notifObject.duration = Constants.DEFAULT_WAIT_ERROR_NOTIFICATIONS;
    }
    this.$notify(notifObject);
  }
  private globalCoefficients: Array<any> = [];
  /**
   * Validate the bop by checking if at least an operation is fully suplied in operation principal and diverse steps
   * return an error message and false when encountring the first:
   *      .Operation group without operation
   *      .an operation left blanck without selecting an operation
   *      .coefficient not suplied (left empty)
   */
  private validateBop(
    bop: Bop,
    subBop?: SubBop,
    triggerNotif: boolean = true,
    subBopIdentifier?: string,
  ): boolean {
    let status = true;
    let message = "";

    // Validate pointValue for Tuyauterie Métallique
    if (
      this.isTuyauterieMetalique &&
      (!bop.pointValue || bop.pointValue <= 0)
    ) {
      status = false;
      message = this.$t("bopSetup.validationPointValueMissing").toString();
    }

    // Validating globalCoefficients
    let isGlobalCoefficientsSet = true;
    let isOperationPrincipalsSet = true;
    let isOperationDiversesSet = true;
    // Validating operation principal groups
    const container: any = subBop ?? this.getActiveOpsContainer();

    for (let i = 0; i < this.globalCoefficients.length; i++) {
      const coeffKey = this.globalCoefficients[i].key;
      const selectedGlobalCoefficients = this.isMultiEchafaudage
        ? container.multipleSelectedGlobalCoefficients
        : container.selectedGlobalCoefficients;
      if (
        !selectedGlobalCoefficients ||
        !selectedGlobalCoefficients[coeffKey]
      ) {
        isGlobalCoefficientsSet = false;
        break;
      }
    }

    if (status) {
      if (!container.operationPrincipalGroups?.length) {
        message = this.$t(
          "bopSetup.ValidationEmptyOperationsErrorMessage",
        ).toString();
        status = false;
      }
      for (let i = 0; i < container.operationPrincipalGroups?.length; i++) {
        let checkQuantity = false;

        const og: OperationGroup = container.operationPrincipalGroups[i];
        if (og.canBeDeleted || og.isFourniture) continue;
        // if no operation were selected in a group
        if (og.operations.length === 0) {
          isOperationPrincipalsSet = false;
          break;
        } else {
          if (!status) break;
          for (let j = 0; j < og.operations.length; j++) {
            const operation: BopOperation = og.operations[j];
            checkQuantity = checkQuantity
              ? checkQuantity
              : operation.quantity > 0 ||
                operation.operation.operationFamilyKey === "viroles";
            //checking if there is an empty operation
            if (!operation.operationId) {
              status = false;
              message = this.$t(
                "bopSetup.summary.completePrincipalOperations",
              ).toString();
              break;
            } else {
              if (!status) break;
              //verifying if all the coefficients were suplied
              for (let k in operation.coefficientSelectedValues) {
                const coeff = operation.coefficientSelectedValues[k];
                if (
                  !coeff ||
                  (!coeff.coefficientOptionId &&
                    coeff.value !== 0 &&
                    !coeff.value)
                ) {
                  status = false;
                  message = this.$t(
                    "bopSetup.ValidationCoefficientErrorMessage",
                  ).toString();
                  break;
                }
              }
            }
          }
          if (!checkQuantity) {
            status = false;
            message = this.$t(
              "bopSetup.ValidationEmptyOperationsErrorMessage",
            ).toString();
          }
        }
        // if there is an error parse the error message to print the step and the group where the error is detected
        if (!status) {
          if (message.indexOf("{step}") !== -1) {
            message = message.replace(
              "{step}",
              this.$t("bop.operationPrincipal.stepTitle").toString(),
            );
          }
          if (message.indexOf("{operationGroup}") !== -1) {
            message = message.replace(
              "{operationGroup}",
              og.operationFamilyName ??
                `${this.$t(
                  "bopSetup.operationPrincipal.operationGroup",
                ).toString()} ${i + 1}`,
            );
          }
          break;
        }
      }
    }
    // do not validate the operation diverse step since we found errors in the operation principal step
    if (status) {
      //validating op diverse
      for (let i = 0; i < container.operationDiverseGroups?.length; i++) {
        const og: OperationGroup = container.operationDiverseGroups[i];
        // if no operation diverse is selected
        if (og.operations.length === 0) {
          isOperationDiversesSet = false;
          break;
        } else {
          for (let j = 0; j < og.operations.length; j++) {
            const operation: BopOperation = og.operations[j];
            //checking if there is an empty operation
            if (!operation.operationId) {
              status = false;
              message = this.$t(
                "bopSetup.summary.completeDiverseOperations",
              ).toString();
              break;
            }
          }
        }
        // if there is an error parse the error message to print the step where the error is detected
        if (!status) {
          if (message.indexOf("{step}") !== -1) {
            message = message.replace(
              "{step}",
              this.$t("bop.operationDiverse.stepTitle").toString(),
            );
          }
          break;
        }
      }
    }
    if (!isOperationPrincipalsSet && !isOperationDiversesSet) {
      message = this.$t(
        "bopSetup.ValidationEmptyOperationsErrorMessage",
      ).toString();
      status = false;
    } else if (isOperationPrincipalsSet && !isGlobalCoefficientsSet) {
      message = this.$t(
        "bopSetup.validationEmptyGlobalCoefficientsErrorMessage",
      ).toString();
    }
    message = subBopIdentifier ? subBopIdentifier + ": " + message : message;
    if (!status && triggerNotif) {
      this.notify(
        this.$t("bopSetup.validationErrorTitle").toString(),
        message,
        "error",
      );
    }

    return status;
  }

  public async exportBopPdf(): Promise<void> {
    if (Guid.parse(this.bop.id).isEmpty()) {
      this.$notify({
        group: "globalError",
        type: "error",
        title: this.$t("bopSetup.exportError.title").toString(),
        text: this.$t(
          "bopSetup.exportError.bopMustBeCreatedBeforeExport",
        ).toString(),
        duration: Constants.DEFAULT_WAIT_ERROR_NOTIFICATIONS,
      });
      return;
    }

    this.isExportingPdf = true;
    const bopId = this.bop.id;
    const bopNumber = this.bop.bopNumber;

    try {
      // Construct the BOP link dynamically
      const baseUrl = `${location.origin}/bops`;
      const bopLink = `${baseUrl}/${bopId}`;

      // Prepare the payload
      const generatePdfDto = {
        bopLink: bopLink,
        languageCode: this.$i18n.locale, // Assuming `this.$i18n.locale` contains the current language code
      };

      const response = await BopService.downloadPdf(bopId, generatePdfDto);
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `BOP_${bopNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.$notify({
        group: "bopNotificationGroup",
        type: "success",
        title: this.$t("bopSetup.exportSuccess.title").toString(),
        text: this.$t("bopSetup.exportSuccess.message").toString(),
      });
    } catch (error) {
      console.error("Error exporting PDF:", error);
      this.$notify({
        group: "globalError",
        type: "error",
        title: this.$t("bopSetup.exportError.title").toString(),
        text: this.$t("bopSetup.exportError.message").toString(),
        duration: Constants.DEFAULT_WAIT_ERROR_NOTIFICATIONS,
      });
    } finally {
      this.isExportingPdf = false;
    }
  }

  public async exportFebPdf(): Promise<void> {
    if (Guid.parse(this.bop.id).isEmpty()) {
      this.$notify({
        group: "globalError",
        type: "error",
        title: this.$t("bopSetup.exportError.title").toString(),
        text: this.$t(
          "bopSetup.exportError.bopMustBeCreatedBeforeExport",
        ).toString(),
        duration: Constants.DEFAULT_WAIT_ERROR_NOTIFICATIONS,
      });
      return;
    }

    this.isExportingPdf = true;
    const bopId = this.bop.id;
    const bopNumber = this.bop.bopNumber;

    try {
      const baseUrl = `${location.origin}/bops`;
      const bopLink = `${baseUrl}/${bopId}?exportFebOnly=true`;

      const generatePdfDto = {
        bopLink: bopLink,
        languageCode: this.$i18n.locale,
      };

      const response = await BopService.downloadPdf(bopId, generatePdfDto);
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `FEB_${bopNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.$notify({
        group: "bopNotificationGroup",
        type: "success",
        title: this.$t("bopSetup.exportSuccess.title").toString(),
        text: this.$t("bopSetup.feb.exportFebSuccess").toString(),
      });
    } catch (error) {
      console.error("Error exporting FEB PDF:", error);
      this.$notify({
        group: "globalError",
        type: "error",
        title: this.$t("bopSetup.exportError.title").toString(),
        text: this.$t("bopSetup.exportError.message").toString(),
        duration: Constants.DEFAULT_WAIT_ERROR_NOTIFICATIONS,
      });
    } finally {
      this.isExportingPdf = false;
    }
  }

  public onCaloTuyauterieOperationsChange(caloTuyauterieOperations) {
    this.caloTuyauterieOperations = caloTuyauterieOperations;
  }
  private mapValidationToBop() {
    if (this.isCreatedBySapAndEditable) {
      const validation = this.$store.state.validation;
      this.bop.sapEPIServiceNumber = validation.sapEPIServiceNumber;
      this.bop.sapPrestationServiceNumber =
        validation.sapPrestationServiceNumber;
      this.bop.sapSupplementServiceNumber =
        validation.sapSupplementServiceNumber;
      this.bop.imputationType = validation.imputationType;
      this.bop.purchaseOrder = validation.purchaseOrder;
      this.bop.otNumber = validation.order;
      this.bop.itemNumber = validation.itemNumber;
      this.bop.materialCost = validation.materialCost;
      this.bop.rentingCost = validation.rentingCost;
      this.bop.subcontractingCost = validation.subcontractingCost;
      this.bop.studyCost = validation.studyCost;
      this.bop.costCenter = validation.costCenter;
      this.bop.wbsElement = validation.WBSelement;
    }
  }

  //TODO Voir pour refacto les sauvegarde des processus SAP
  public async save(action?: string) {
    this.saveFinished = false;
    if (
      this.saveDisabled ||
      (!this.isEditable &&
        this.bop.bopStatusKey !== BopStatus.AwaitingLaunchWorkApproval &&
        !this.showSaveForOtpValidation)
    ) {
      return;
    }
    this.saveDisabled = true;
    const oldBopStatus = this.bop.bopStatusKey;

    try {
      let message = this.$t("bopSetup.saveSuccessMessage").toString();
      if (this.isCalorifugeageTuyauterieBop) {
        const operationGroups: Array<OperationGroup> = [];
        this.caloTuyauterieOperations.forEach((og) => {
          const operationGroup = new OperationGroup();
          operationGroup.number = og.number;
          operationGroup.id = og.id;
          for (let i in og.coefficientSelectedValues) {
            operationGroup.selectedGroupCoefficients[i] =
              og.coefficientSelectedValues[i].coefficientOptionId ??
              og.coefficientSelectedValues[i].value?.toString();
          }
          operationGroup.operations = Object.values(og.operations).map(
            (o: any) => ({
              ...o,
              coefficientSelectedValues: og.coefficientSelectedValues,
              operation: o,
            }),
          );
          operationGroups.push(operationGroup);
        });
        const container: any = this.getActiveOpsContainer();
        container.operationPrincipalGroups = operationGroups;
        this.$store.commit("changeBop", this.bop);
      }
      // Triggers on summary page
      if (this.canValidateBop && !this.showSaveForOtpValidation) {
        if (this.isMultiEchafaudage) {
          //We need to validate every subBops before saving/approving the BOP
          for (const feb of this.bop.febs!) {
            for (const subBop of feb.subBops!) {
              let subBopIdentifier = `${this.bop.bopNumber || "?"}.${feb.index}.${subBop?.sequenceOrder}`;

              if (!this.validateBop(this.bop, subBop, true, subBopIdentifier)) {
                this.saveDisabled = false;
                this.saveFinished = true;
                return;
              }
            }
          }
        }
        if (!this.validateBop(this.getActiveOpsContainer())) {
          this.saveDisabled = false;
          this.saveFinished = true;
          return;
        }
        if (!this.bop.isCreatedBySap) {
          // Define the keys for travaux neufs and arrêts
          const travauxNeufsOrArretsKeys = ["travauxNeufs", "arrets"];
          if (action && action === "approve") {
            this.bop.bopStatusKey = BopStatus.AcceptLaunchWork;
          } else if (
            travauxNeufsOrArretsKeys.includes(
              this.typeTravaux.find((type) => type.id === this.bop.typeTravaux)
                ?.key,
            )
          ) {
            this.bop.bopStatusKey = BopStatus.AwaitingLaunchWorkApproval;
          } else {
            this.bop.bopStatusKey = BopStatus.AcceptLaunchWork;
          }
        }
        message = this.$t("bopSetup.validationSuccessMessage").toString();
      }

      const saveBopResponse = await BopService.save(this.bop, false, action);

      // if its not created by sap or if its created by sap and the bop was send successufly
      let isSuccess = true;
      if (this.bop.isCreatedBySap && this.canValidateBop) {
        isSuccess =
          (
            await SapApiService.validateBop(
              this.bop.id,
              this.facturationValidation,
            )
          ).status === 200;
        if (isSuccess) {
          this.bop.bopStatusKey = BopStatus.WaitingForValidation;
          this.notify(
            this.$t("bopSetup.sendForValidationSuccessTitle").toString(),
            this.$t("bopSetup.sendForValidationSuccessMessage").toString(),
            "success",
          );
        }
      }
      if (saveBopResponse.status === 200 && isSuccess) {
        this.saveDisabled = false;
        if (
          this.bop.bopStatusKey === BopStatus.AcceptLaunchWork &&
          this.facturationStepIndex === -1
        ) {
          this.steps.push(this.facturationStepConfig);
        }

        if (
          this.isMultiEchafaudage &&
          Object.keys(saveBopResponse.data.subBopsId).length
        ) {
          this.bop.febs?.forEach((feb) => {
            const savedFebId = saveBopResponse.data.febsId[feb.id];
            if (savedFebId) {
              let isSelectedFeb = this.selectedFebId === feb.id;
              feb.id = savedFebId;
              if (isSelectedFeb) {
                this.$store.commit("changeSelectedFebId", feb.id);
              }
            }
            let isFebSelected = this.selectedFebId === feb.id;

            feb.subBops?.forEach((subBop) => {
              const savedSubBop = saveBopResponse.data.subBopsId[subBop.id];
              if (savedSubBop) {
                let isSelected = this.selectedSubBopId === subBop.id;
                subBop.id = savedSubBop;
                if (isSelected) {
                  this.$store.commit("changeSelectedSubBopId", subBop.id);
                }
              }
            });
          });
        }

        if (this.bop.id !== Guid.createEmpty().toString()) {
          await this.loadOperations(this.bop);
        }

        this.notify(
          this.$t("bopSetup.saveSuccessTitle").toString(),
          message,
          "success",
        );
        const bopId: string = saveBopResponse.data.bopId;
        this.saveFinished = true;
        this.$store.commit("changeHasBeenModified", false);
        if (
          this.bop.bopStatusKey === BopStatus.AcceptLaunchWork ||
          this.bop.bopStatusKey === BopStatus.AwaitingLaunchWorkApproval
        ) {
          await this.$router.push({
            name: "Bop",
          });
        } else if (
          this.$route.params.bopId !== bopId ||
          this.stepIndex.toString() !== this.$route.query.index
        ) {
          this.$store.commit("changeHasBeenModified", false);
          const prevRouteName = this.$route.name;

          await this.$router.push({
            name: "BopSetupUpdate",
            params: { bopId },
            query: { index: this.stepIndex.toString() },
          });
          if (prevRouteName === "BopSetupUpdate") {
            this.startIndex = this.stepIndex;
            this.refreshWizard();
          }
        } else {
          this.startIndex = this.stepIndex;
          this.refreshWizard();
        }
      }
    } catch (err) {
      this.saveFinished = true;
      this.bop.bopStatusKey = oldBopStatus;
      this.saveDisabled = false;

      const detail = (err as { response: { data: { detail: string } } })
        .response.data.detail;

      const errorCodes: Array<string> = [
        "moreThan10Files",
        "fileExceedsMaxSize",
      ];

      let text = this.$t(`bop.saveErrorMessage`).toString();

      if (errorCodes.includes(detail)) {
        text = this.$t(`bop.errors.${detail}`).toString();
      } else if (detail === "febFieldsNotSpecified") {
        text = this.$t(`bop.errors.febMandatoryFieldsNotSpecified`).toString();
      }

      this.notify(this.$t(`bop.saveErrorTitle`).toString(), text, "error");
    }
  }

  public async declineBop() {
    const modalResponse = await this.$bvModal.msgBoxConfirm(
      router.app.$t("modal.cancelBopMessage").toString(),
      {
        title: router.app.$t("modal.cancelBopTitle").toString(),
        size: "lg",
        buttonSize: "lg",
        okVariant: "primary",
        cancelVariant: "secondary",
        okTitle: router.app.$t("modal.cancelBopYes").toString(),
        cancelTitle: router.app.$t("modal.cancelBopNo").toString(),
        dialogClass: "modal-app",
        footerClass: "p-2",
        hideHeaderClose: false,
        centered: true,
      },
    );
    if (modalResponse) {
      try {
        const bopId = this.bop.id;
        const response = await BopService.cancelBop(bopId);
        await this.$router.push({
          name: "Bop",
        });
        if (response.status === 200) {
          this.bop.bopStatusKey = BopStatus.Declined;
          this.bop.bopStatusName = this.statuses[BopStatus.Declined];
          this.$notify({
            group: "globalError",
            type: "success",
            title: this.$t("bopSetup.declineSuccessTitle").toString(),
            text: this.$t("bopSetup.declineSuccessMessage").toString(),
            duration: Constants.DEFAULT_WAIT_ERROR_NOTIFICATIONS,
          });
        }
      } catch {}
    }
  }

  private get statuses(): Object {
    return this.$store.state.statuses;
  }

  public async approveBop() {
    await this.save("approve");
  }

  private async loadOperations(bop: Bop) {
    const isMulti = this.isMultiEchafaudage;
    const subBopId = this.selectedSubBopId;
    if (
      Bop.readOnlyStatuses.includes(bop.bopStatusKey) &&
      !this.isEdit
      // &&
      // !this.isTuyauterieMetalique &&
      // !this.isMultiEchafaudage
    ) {
      const bopArchive = (await BopArchiveService.getBopArchive(bop.id)).data;
      if (!bopArchive) return;
      // store archive snapshot for read-only binding across steps
      this.$store.commit("changeBopArchive", bopArchive);
      const container: any = this.getActiveOpsContainer();

      const selectedSubBopArchive =
        isMulti && subBopId
          ? (bopArchive.febs
              ?.flatMap((feb) => feb.subBops)
              .find((sb) => sb?.id === subBopId) ?? null)
          : null;

      if (isMulti && selectedSubBopArchive) {
        container.operationDiverseGroups =
          selectedSubBopArchive.operationDiverseGroups ?? [
            new OperationGroup(),
          ];
        container.operationPrincipalGroups =
          selectedSubBopArchive.operationPrincipalGroups;
        container.operationSuppGroups =
          selectedSubBopArchive.operationSuppGroups;
        container.selectedGlobalCoefficients =
          selectedSubBopArchive.selectedGlobalCoefficients;
        container.heightSupplements = selectedSubBopArchive.heightSupplements;
        this.operationPrinciaplLoaded = true;
        // notify listeners that archive groups are bound for current SubBOP
        this.$root.$emit("subbop-ops-loaded", {
          type: "principal",
          subBopId: subBopId,
        });
        this.$root.$emit("subbop-ops-loaded", {
          type: "diverse",
          subBopId: subBopId,
        });
        this.$root.$emit("subbop-ops-loaded", {
          type: "supp",
          subBopId: subBopId,
        });
        return;
      }
      container.operationDiverseGroups = bopArchive.operationDiverseGroups ?? [
        new OperationGroup(),
      ];
      container.operationPrincipalGroups = bopArchive.operationPrincipalGroups;
      container.operationSuppGroups = bopArchive.operationSuppGroups;
      container.selectedGlobalCoefficients =
        bopArchive.selectedGlobalCoefficients;
      container.heightSupplements = bopArchive.heightSupplements;
      this.operationPrinciaplLoaded = true;
      // notify listeners that archive groups are bound for BOP-level container
      this.$root.$emit("subbop-ops-loaded", {
        type: "principal",
        subBopId: subBopId,
      });
      this.$root.$emit("subbop-ops-loaded", {
        type: "diverse",
        subBopId: subBopId,
      });
      this.$root.$emit("subbop-ops-loaded", {
        type: "supp",
        subBopId: subBopId,
      });
      return;
    }

    const container: any = this.getActiveOpsContainer();
    if (
      !container.operationPrincipalGroups ||
      container.operationPrincipalGroups.length === 0
    ) {
      // loading operation principal
      const operationPrincipalGroupsResponse =
        isMulti && subBopId
          ? await BopService.getSubBopOperationGroups(
              subBopId,
              OperationType.OperationPrincipal,
              this.language,
            )
          : await BopService.getBopOperationGroups(
              bop.id,
              OperationType.OperationPrincipal,
              this.language,
            );
      if (operationPrincipalGroupsResponse.status === 200) {
        this.operationPrinciaplLoaded = true;
        container.operationPrincipalGroups =
          operationPrincipalGroupsResponse.data;
        // notify listeners that principal groups are loaded for current container
        this.$root.$emit("subbop-ops-loaded", {
          type: "principal",
          subBopId: subBopId,
        });
      }
    }

    // loading operation diverse
    const container2: any = this.getActiveOpsContainer();
    container2.operationDiverseGroups = (
      isMulti && subBopId
        ? await BopService.getSubBopOperationGroups(
            subBopId,
            OperationType.OperationDiverse,
            this.language,
          )
        : await BopService.getBopOperationGroups(
            bop.id,
            OperationType.OperationDiverse,
            this.language,
          )
    ).data;
    container2.operationDiverseGroups = container2.operationDiverseGroups.length
      ? container2.operationDiverseGroups
      : [new OperationGroup()];
    this.$root.$emit("subbop-ops-loaded", {
      type: "diverse",
      subBopId: subBopId,
    });

    // loading operation supp
    const container3: any = this.getActiveOpsContainer();
    container3.operationSuppGroups = (
      isMulti && subBopId
        ? await BopService.getSubBopOperationGroups(
            subBopId,
            OperationType.OperationSupplementaire,
            this.language,
          )
        : await BopService.getBopOperationGroups(
            bop.id,
            OperationType.OperationSupplementaire,
            this.language,
          )
    ).data;
    container3.operationSuppGroups = container3.operationSuppGroups.length
      ? container3.operationSuppGroups
      : [new OperationGroup()];
    this.$root.$emit("subbop-ops-loaded", {
      type: "supp",
      subBopId: subBopId,
    });
  }

  private async loadData() {
    // console.group("im being laoded");
    this.steps = [
      {
        slot: "generalInformations",
        title: this.$t("bop.generalInformations.stepTitle").toString(),
        requiredFieldsKeys: [
          {
            translationKey: "bopSetup.bopName",
            elementKey: "name",
            isRequiredOnSimulation: true,
            validation: null,
          },
          {
            translationKey: "bopSetup.sites",
            elementKey: "siteId",
            isRequiredOnSimulation: false,
            validation: null,
          },
          {
            translationKey: "bopSetup.ordergivers",
            elementKey: "orderGiverUserId",
            isRequiredOnSimulation: false,
            validation: null,
          },
          {
            translationKey: "bopSetup.entreprises",
            elementKey: "entrepriseId",
            isRequiredOnSimulation: false,
            validation: null,
          },
          {
            translationKey: "bopSetup.bopModelChoice",
            elementKey: "bopModelId",
            isRequiredOnSimulation: true,
            validation: null,
          },
        ],
      },

      {
        slot: "operationPrincipal",
        title: this.$t("bop.operationPrincipal.stepTitle").toString(),
        requiredFieldsKeys: [],
      },
      {
        slot: "operationDiverse",
        title: this.$t("bop.operationDiverse.stepTitle").toString(),
        requiredFieldsKeys: [],
      },
      {
        slot: "operationSupp",
        title: this.$t("bop.operationSupp.stepTitle").toString(),
        requiredFieldsKeys: [],
      },
      {
        slot: "summary",
        title: this.$t("bop.summary.stepTitle").toString(),
        requiredFieldsKeys: [],
        // btnSaveText: this.$t("wizard.validate").toString()
      },
    ];
    this.typeTravaux = (
      await TypeTravauxService.listTypeTravaux(this.language)
    ).data;

    if (
      this.bop.bopModelKey === BopModelEnum.Echafaudage ||
      this.bop.bopModelKey === BopModelEnum.MultiEchafaudage
    ) {
      this.steps.splice(1, 0, this.febStepConfig);
    }
    if (
      this.isMultiEchafaudage &&
      this.bop.febs?.length &&
      this.selectedSubBopId === null
    ) {
      const firstFeb = this.bop.febs[0] ?? null;
      const firstSubBop = firstFeb.subBops?.[0] || null;
      this.$store.commit("changeSelectedSubBopId", firstSubBop?.id || null);
      this.$store.commit("changeSelectedFebId", firstFeb?.id || null);
    }
    const bopStatuses = (await BopStatusService.getAllStatuses(this.language))
      .data;
    this.$store.commit("loadBopStatuses", bopStatuses);

    this.$store.commit("changeBopOperationOptions", null);
    this.$store.commit("changeSelectedGlobalCoefficients", {});
    const bopId: any = this.$route.params.bopId;
    let validation: Validation = new Validation();
    if (bopId) {
      const bop: any = await BopService.getBopDomain(bopId);
      this.$store.commit("changeBop", bop);
      if (bop.bopModelId != null) {
        const bm = (
          await BopModelService.getBopModel(bop.bopModelId, this.language)
        ).data;
        this.$store.commit("changeSelectedBopModel", bm);
        this.globalCoefficients = (
          await CoefficientService.getCoefficientByTypeAndBopModel(
            bop.bopModelId,
            CoefficientTypeEnum.T4,
            this.language,
          )
        ).data;
      }

      //loading operations principal
      await this.loadOperations(bop);

      // Prefetch principal operations for all SubBOPs in MultiEchafaudage
      // to support data display in SubBops modal without requiring selection.
      if (this.isMultiEchafaudage) {
        await this.prefetchAllSubBopsPrincipalOps();
      }

      this.$store.commit("changeBop", bop);
      this.$store.dispatch("fetchMinimumFacturable");

      if (this.bop.isCreatedBySap) {
        this.steps.splice(
          this.steps.findIndex((s) => s.slot === "operationSupp") + 1,
          0,
          this.facturationSapStepConfig,
        );
      }

      if (
        (this.bop.bopStatusKey === BopStatus.AcceptLaunchWork ||
          this.bop.bopStatusKey === BopStatus.Finished ||
          this.bop.bopStatusKey === BopStatus.Archived) &&
        this.isFacturationVisibleForOrderGiver
      ) {
        this.steps.push(this.facturationStepConfig);
      }
    } else {
      this.operationPrinciaplLoaded = true;
    }

    this.$store.commit("changeValidation", validation);
    await this.onEnvSpecificParamsChanged();
    // console.groupEnd();
  }
  /**
   * Prefetch principal operation groups for every SubBOP when in MultiEchafaudage.
   * This ensures SubBops modal has data available even before a SubBOP is selected.
   */
  private async prefetchAllSubBopsPrincipalOps(): Promise<void> {
    const febs: any[] = (this.bop.febs || this.bop.febs || []) as any[];
    if (!Array.isArray(febs) || !febs.length) return;

    try {
      let bopArchive: any | null = null;
      let isReadonly = Bop.readOnlyStatuses.includes(this.bop.bopStatusKey);
      if (isReadonly) {
        bopArchive = (await BopArchiveService.getBopArchive(this.bop.id)).data;
      }
      // Editable states: fetch principal operation groups per SubBOP
      const tasks: Promise<void>[] = [];
      febs.forEach((f: any) => {
        const subBops: any[] = f?.subBops || f?.SubBops || [];
        (subBops || []).forEach((sb: any) => {
          tasks.push(
            (async () => {
              try {
                if (isReadonly) {
                  const archivedSubBop = bopArchive?.febs
                    ?.find((af: any) => af.id === f.id)
                    ?.subBops?.find((asb: any) => asb.id === sb.id);
                  if (archivedSubBop?.operationPrincipalGroups) {
                    this.$set(
                      sb,
                      "operationPrincipalGroups",
                      archivedSubBop.operationPrincipalGroups,
                    );
                    return;
                  }
                }
                const res = await BopService.getSubBopOperationGroups(
                  sb.id,
                  OperationType.OperationPrincipal,
                  this.language,
                );
                if (res && res.status === 200 && Array.isArray(res.data)) {
                  this.$set(sb, "operationPrincipalGroups", res.data);
                }
              } catch (e) {
                // best-effort prefetch; ignore individual failures
              }
            })(),
          );
        });
      });
      await Promise.all(tasks);
    } catch (err) {
      // Silent failure; modal will degrade gracefully without prefetch data
    }
  }

  async canApproveBop(): Promise<boolean> {
    const bopId = this.bop.id;
    return await userService.canApproveBop(bopId, this.currentUser.id);
  }

  @Watch("selectedFebId")
  OnSelectedFebChanged() {
    if (!this.validateStep(1, false)) {
      let currentStepIndex = this.stepIndex;

      this.stepIndex = 1;
      this.setNavigationBehavour(currentStepIndex, 1);
      (<any>this.$refs.bopSetupWizard).navigate(1);
    }
  }
  /**
   * ######
   * Hooks
   * ######
   */

  private async created() {
    await BopStatusService.getAllStatuses(this.$i18n.locale).then(
      (response) => {
        this.$store.commit("changeStatuses", response.data);
      },
    );
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("exportFebOnly") === "true") {
      document.body.classList.add("feb-export");
    }
    this.loadData().then(async () => {
      if (!this.$store.state.selectedBopModel && this.bop.bopModelId) {
        BopModelService.getBopModel(this.bop.bopModelId, this.language).then(
          (response) => {
            const bm = response.data;
            this.$store.commit("changeSelectedBopModel", bm);
          },
        );
      }

      if (this.$route.query.index) {
        let stepIndex = parseInt(this.$route.query.index.toString());
        if (!isNaN(stepIndex) && stepIndex < this.steps.length) {
          // steps validation
          let isValidatePreviousSteps = true;
          for (let i = 0; i <= stepIndex; i++) {
            if (!this.validateStep(i, false)) {
              isValidatePreviousSteps = false;
              stepIndex = i;
              break;
            }
          }

          this.startIndex = stepIndex;
          this.stepIndex = stepIndex;
          if (
            this.canValidateBop ||
            (this.bop.isCreatedBySap && stepIndex === this.facturationStepIndex)
          ) {
            this.saveButtonText = this.$t("wizard.validate").toString();
          }
        }
        this.isLoaded = true;
        this.$nextTick().then(() => {
          setTimeout(() => {
            this.$store.commit("changeHasBeenModified", false);
            this.isVisible = true;
          }, 200);
        });
      } else {
        this.isVisible = true;
        this.isLoaded = true;
      }
      this.userCanApproveBop = await this.canApproveBop();
      this.showSaveForOtpValidation = await this.isApprovedEditable();
      if (this.showSaveForOtpValidation) {
        this.saveButtonText = this.$t("wizard.save").toString();
      }
    });
  }
  private destroyed() {
    this.$store.commit("changeBop", new Bop());
    this.$store.commit("changeMinimumFacturable", null);
    this.$store.commit("changeSelectedBopModel", null);
  }
  /**
   * #####
   * Watchers
   * #####
   */
  @Watch("selectedSubBopId")
  private async onSelectedSubBopChanged(
    newId: string | null,
    oldId: string | null,
  ) {
    if (!this.isMultiEchafaudage) return;
    if (!newId) return;
    // Load ops only if the target container is not initialized yet
    const container: any = this.getActiveOpsContainer();
    const hasPrincipal =
      Array.isArray(container.operationPrincipalGroups) &&
      container.operationPrincipalGroups.length > 0;
    const hasDiverse =
      Array.isArray(container.operationDiverseGroups) &&
      container.operationDiverseGroups.length > 0 &&
      container.operationDiverseGroups[0].operations.length > 0;
    const hasSupp =
      Array.isArray(container.operationSuppGroups) &&
      container.operationSuppGroups.length > 0 &&
      container.operationSuppGroups[0].operations.length > 0;
    if (!(hasDiverse || hasSupp)) {
      await this.loadOperations(this.bop);
    } else {
      this.$root.$emit("subbop-ops-loaded", {
        type: "principal",
        subBopId: this.selectedSubBopId,
      });
    }
  }
  @Watch("bop.id")
  async onBopIdChange(newBopId: string) {
    if (!newBopId || newBopId === Guid.createEmpty().toString()) {
      return;
    }

    this.userCanApproveBop = await this.canApproveBop();
  }
  @Watch("bop.bopModelKey")
  onBopModelKeyChange() {
    if (
      (this.bop.bopModelKey === BopModelEnum.Echafaudage ||
        this.bop.bopModelKey === BopModelEnum.MultiEchafaudage) &&
      this.steps.findIndex((s) => s.slot === this.febStepConfig.slot) === -1
    ) {
      this.steps.splice(1, 0, this.febStepConfig);
    } else {
      const febSlotIndex = this.steps.findIndex(
        (s) => s.slot === this.febStepConfig.slot,
      );
      if (febSlotIndex !== -1) {
        this.steps.splice(febSlotIndex, 1);
      }
    }
    if (
      this.isMultiEchafaudage &&
      this.bop.febs?.length &&
      this.selectedSubBopId === null
    ) {
      const firstFeb = this.bop.febs[0] ?? null;
      const firstSubBop = firstFeb.subBops?.[0] || null;
      this.$store.commit("changeSelectedSubBopId", firstSubBop?.id || null);
      this.$store.commit("changeSelectedFebId", firstFeb?.id || null);
    }
    this.$store.commit("changeHasBeenModified", true);
  }

@Watch('bop.siteId')
@Watch('bop.typeTravaux')
@Watch('bop.bopModelId')
private async onEnvSpecificParamsChanged() {
  console.log('ici');
  const { bopModelId, siteId, typeTravaux: typeTravauxId } = this.bop;

  if (!bopModelId ||
      this.bop.bopModelKey !== BopModelEnum.Echafaudage) {
    return this.clearEnvOptions();
  }

  try {
    const { data: coeff } =
      await CoefficientService.getCoefficientByKeyAndBopModel(
        bopModelId,
        'EnvKey',
        this.language
      );

    if (!coeff?.id) {
      return this.clearEnvOptions();
    }

    if (!siteId || !typeTravauxId) {
      return this.clearEnvOptions();
    }

    const { data: allValues = [] } =
      await CoefficientOptionService.GetCoefficientOptionsByCoefficientSiteType(
        coeff.id,
        siteId,
        typeTravauxId,
        this.language
      );

    const options = allValues.map((v: any) => ({
      id: v.id || v.trackId || v.coefficientId,
      coefficientId: coeff.id,
      coefficientKey: coeff.key,
      value: v.value,
      label: v.name || `${v.value}`,
    }));

    this.$store.commit('changeEnvSpecificOptions', options);

    this.upsertGlobalCoefficient(coeff.key || 'EnvKey', options, coeff.id);

  } catch {
    this.clearEnvOptions();
  }
}

  @Watch("language")
  private onLanguageChange() {
    this.isLoaded = false;
    this.loadData().then(() => {
      this.startIndex = this.stepIndex ?? 0;
      if (this.bop.bopModelId) {
        BopModelService.getBopModel(this.bop.bopModelId, this.language).then(
          (response) => {
            const bm = response.data;
            this.bop.bopModelName = bm.name;
            this.$store.commit("changeHasBeenModified", false);
          },
        );
      }
      if (this.bop.siteId) {
        SitesServices.getTranslatedSite(this.bop.siteId, this.language).then(
          (response) => {
            const site = response.data;
            this.bop.siteName = site.translation.name;
            this.$store.commit("changeHasBeenModified", false);
          },
        );
      }
      this.isLoaded = true;
      this.$nextTick().then(() => {
        setTimeout(() => {
          this.$store.commit("changeHasBeenModified", false);

          this.isVisible = true;
        }, 200);
      });
    });
  }
}
</script>
<style lang="scss">
.custom-container {
  padding: 5% 0;
  position: relative;
}
.height-60vh {
  height: 60vh;
}
.loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  bottom: 0;
  height: 100%;
  z-index: 999999;
  background: #ffffff70;
}
</style>
